// Client "Promotions": every promotion this buyer can use (live now or starting soon), in their own
// currency, with a plain-English explanation of how it's applied and a worked example per tier.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import { fetchMyPromotions } from '../api/me.js';
import { fmtMoney } from '../lib/matrix.js';
import { fmtWhole, offerLine, timingLine } from '../lib/promotions.js';

function TierTable({ p, currency }) {
  const rows = p.tiers.map((t, i) => {
    const next = p.tiers[i + 1];
    const range = next ? `${fmtWhole(t.minSubtotal, currency)} – ${fmtWhole(next.minSubtotal - 1, currency)}` : `${fmtWhole(t.minSubtotal, currency)} or more`;
    // Worked example: an order a little above the threshold.
    const example = t.minSubtotal > 0 ? Math.round(t.minSubtotal * 1.1) : 500;
    return { ...t, range, example };
  });
  return (
    <div className="mt-4 overflow-hidden rounded-md border border-line">
      <table className="w-full text-sm">
        <thead className="bg-surface-raised text-left text-xs uppercase tracking-wide text-text-muted">
          <tr>
            <th className="px-3 py-2 font-medium">Order value</th>
            <th className="px-3 py-2 font-medium">You save</th>
            <th className="hidden px-3 py-2 font-medium sm:table-cell">For example</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {p.tiers[0]?.minSubtotal > 0 && (
            <tr className="text-text-muted">
              <td className="px-3 py-2">Under {fmtWhole(p.tiers[0].minSubtotal, currency)}</td>
              <td className="px-3 py-2">—</td>
              <td className="hidden px-3 py-2 sm:table-cell">No discount</td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.minSubtotal}>
              <td className="px-3 py-2 tabular-nums text-text-primary">{r.range}</td>
              <td className="px-3 py-2 font-semibold text-accent">{r.percent}% off</td>
              <td className="hidden px-3 py-2 tabular-nums text-text-secondary sm:table-cell">
                {fmtWhole(r.example, currency)} order → you save {fmtMoney((r.example * r.percent) / 100, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PromotionCard({ p, currency }) {
  const live = p.status === 'LIVE';
  return (
    <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-text-primary">{p.name}</h2>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${live ? 'bg-success-light text-success' : 'bg-warning-light text-warning'}`}>
              {live ? 'Live now' : 'Coming soon'}
            </span>
          </div>
          <p className="mt-1 text-sm font-medium text-text-secondary">{offerLine(p, currency)}</p>
          <p className="mt-0.5 text-xs text-text-muted">{timingLine(p)}</p>
        </div>
        {live && (
          <Link to="/catalog" className="shrink-0 rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-white hover:bg-accent-hover">
            Place an order
          </Link>
        )}
      </div>
      {p.description && <p className="mt-3 text-sm text-text-secondary">{p.description}</p>}
      {p.type === 'TIERED' && <TierTable p={p} currency={currency} />}
      {p.converted && (
        <p className="mt-2 text-xs text-text-muted">
          Amounts are shown in {currency} at today’s exchange rate, so they can move slightly from day to day.
        </p>
      )}
    </div>
  );
}

export default function ClientPromotions() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchMyPromotions()
      .then(setData)
      .catch(() => setData({ currency: null, promotions: [] }));
  }, []);

  const promotions = data?.promotions || [];
  const tiered = promotions.some((p) => p.type === 'TIERED');

  return (
    <>
      <PageHeader title="Promotions" subtitle="Savings available on your account — applied automatically when you order here." />

      {data === null ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : promotions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center">
          <p className="text-sm font-medium text-text-primary">No promotions right now</p>
          <p className="mt-1 text-sm text-text-muted">When a promotion is set up for your account it will appear here, and we’ll email you too.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr_300px] lg:items-start">
          <div className="space-y-4">
            {promotions.map((p) => (
              <PromotionCard key={p.id} p={p} currency={data.currency} />
            ))}
          </div>

          <aside className="rounded-lg border border-line bg-surface p-5 text-sm shadow-sm">
            <h2 className="font-semibold text-text-primary">How promotions work</h2>
            <ul className="mt-3 list-disc space-y-2 pl-4 text-text-secondary">
              <li><strong className="text-text-primary">Automatic.</strong> No code needed — the discount is applied when you place your order on this platform.</li>
              <li><strong className="text-text-primary">Based on your goods total.</strong> The order value is your products before shipping, duties and taxes.</li>
              {tiered && <li><strong className="text-text-primary">The whole order gets the higher rate.</strong> Reach a tier and that % comes off everything in the order, not just the amount above it.</li>}
              <li><strong className="text-text-primary">One promotion per order.</strong> If more than one applies, you automatically get the best one.</li>
              <li><strong className="text-text-primary">Ordered during the promotion.</strong> It applies to orders placed between the start and end dates.</li>
              <li><strong className="text-text-primary">Shown on your order and invoice.</strong> You’ll see the discount line on your order confirmation and invoice.</li>
            </ul>
            <p className="mt-4 text-xs text-text-muted">
              Questions? <Link to="/messages" className="text-accent hover:underline">Message us</Link>.
            </p>
          </aside>
        </div>
      )}
    </>
  );
}
